// Browser client for the CRM overlay API (/api/lead-crm).
//
// The Supabase service role key never reaches this side: every call goes
// through a Netlify Function, which enforces the CRM session and validation.

export const CRM_STATUSES = [
  'New',
  'Contacted',
  'Follow-Up',
  'Qualified',
  'Nurture',
  'Won',
  'Lost',
] as const

export type CrmStatus = (typeof CRM_STATUSES)[number]

export const SOCIAL_PLATFORMS = [
  'facebook',
  'instagram',
  'linkedin',
  'tiktok',
  'youtube',
  'x',
  'other',
] as const

export type SocialPlatform = (typeof SOCIAL_PLATFORMS)[number]

export const SOCIAL_PLATFORM_LABELS: Record<SocialPlatform, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  x: 'X / Twitter',
  other: 'Other',
}

export const INTERACTION_TYPES = [
  'facebook-dm',
  'instagram-dm',
  'linkedin-message',
  'phone-call',
  'email',
  'sms',
  'meeting',
  'other',
] as const

export type InteractionType = (typeof INTERACTION_TYPES)[number]

export const INTERACTION_TYPE_LABELS: Record<InteractionType, string> = {
  'facebook-dm': 'Facebook DM',
  'instagram-dm': 'Instagram DM',
  'linkedin-message': 'LinkedIn Message',
  'phone-call': 'Phone Call',
  email: 'Email',
  sms: 'SMS / Text',
  meeting: 'Meeting',
  other: 'Other',
}

export type StaffMember = {
  id: string
  displayName: string
  email: string | null
  active: boolean
}

export type LeadCrmProfile = {
  leadKey: string
  assignedStaffId: string | null
  crmStatus: CrmStatus | null
  crmNote: string | null
  createdAt: string | null
  updatedAt: string | null
}

export type LeadSocialLink = {
  id: string
  leadKey: string
  platform: SocialPlatform
  label: string | null
  url: string
  createdAt: string | null
  updatedAt: string | null
}

export type LeadInteraction = {
  id: string
  leadKey: string
  interactionType: InteractionType
  channel: string | null
  summary: string
  outcome: string | null
  nextAction: string | null
  occurredAt: string
  handledByStaffId: string | null
  // Reserved for the Gmail work: the message an interaction came from.
  gmailMessageId: string | null
  gmailThreadId: string | null
  createdAt: string | null
  updatedAt: string | null
}

export type LeadCrmSnapshot = {
  profile: LeadCrmProfile | null
  socialLinks: LeadSocialLink[]
  interactions: LeadInteraction[]
  staff: StaffMember[]
}

export type SocialLinkInput = {
  platform: SocialPlatform
  label: string
  url: string
}

export type InteractionInput = {
  interactionType: InteractionType
  channel: string
  occurredAt: string
  handledByStaffId: string | null
  summary: string
  outcome: string
  nextAction: string
}

async function readJson<T>(response: Response): Promise<T> {
  try {
    return (await response.json()) as T
  } catch {
    return {} as T
  }
}

async function send<T>(path: string, method: string, body?: unknown): Promise<T> {
  const response = await fetch(path, {
    method,
    credentials: 'include',
    headers: {
      accept: 'application/json',
      ...(body === undefined ? {} : { 'content-type': 'application/json' }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  })

  const payload = await readJson<T & { message?: string; field?: string }>(response)

  if (!response.ok) {
    throw new CrmRequestError(payload.message ?? 'The CRM workspace request failed.', payload.field ?? null)
  }

  return payload
}

/** A failed CRM request; `field` names the form field the server rejected. */
export class CrmRequestError extends Error {
  constructor(message: string, readonly field: string | null) {
    super(message)
  }
}

export type LeadAssignment = {
  assignedStaffId: string
  assignedStaffName: string | null
  crmStatus: CrmStatus | null
}

export type AssignmentSnapshot = {
  /** Keyed by canonical lead key; only assigned leads are present. */
  assignments: Record<string, LeadAssignment>
  staff: StaffMember[]
}

/** Every lead's CRM assignment and the whole team, in one request. */
export async function fetchAssignmentSnapshot(signal?: AbortSignal): Promise<AssignmentSnapshot> {
  const response = await fetch('/api/lead-crm/assignments', {
    method: 'GET',
    credentials: 'include',
    headers: { accept: 'application/json' },
    signal,
  })
  const payload = await readJson<Partial<AssignmentSnapshot>>(response)

  if (!response.ok) throw new Error('Lead assignments could not be loaded.')

  return {
    assignments: payload.assignments ?? {},
    staff: Array.isArray(payload.staff) ? payload.staff : [],
  }
}

export async function fetchLeadAssignments(signal?: AbortSignal): Promise<Record<string, LeadAssignment>> {
  return (await fetchAssignmentSnapshot(signal)).assignments
}

/** Fired after a salesperson is created, so every open view can add them. */
export const STAFF_ADDED_EVENT = 'wolf-forge-staff-added'

export async function createCrmStaff(input: { displayName: string; email: string }): Promise<StaffMember> {
  const payload = await send<{ staff?: StaffMember }>('/api/lead-crm/staff', 'POST', input)

  if (!payload.staff) throw new Error('The salesperson could not be added.')

  return payload.staff
}

export async function fetchLeadCrm(leadKey: string, signal?: AbortSignal): Promise<LeadCrmSnapshot> {
  const response = await fetch(`/api/lead-crm?leadKey=${encodeURIComponent(leadKey)}`, {
    method: 'GET',
    credentials: 'include',
    headers: { accept: 'application/json' },
    signal,
  })

  const payload = await readJson<Partial<LeadCrmSnapshot> & { message?: string }>(response)

  if (!response.ok) {
    throw new Error(payload.message ?? 'The CRM workspace could not be loaded.')
  }

  return {
    profile: payload.profile ?? null,
    socialLinks: Array.isArray(payload.socialLinks) ? payload.socialLinks : [],
    interactions: Array.isArray(payload.interactions) ? payload.interactions : [],
    staff: Array.isArray(payload.staff) ? payload.staff : [],
  }
}

export async function saveLeadCrmProfile(
  leadKey: string,
  changes: Partial<Pick<LeadCrmProfile, 'assignedStaffId' | 'crmStatus' | 'crmNote'>>,
): Promise<LeadCrmProfile> {
  const payload = await send<{ profile?: LeadCrmProfile }>('/api/lead-crm/profile', 'PUT', {
    leadKey,
    ...changes,
  })

  if (!payload.profile) throw new Error('The CRM change could not be saved.')

  return payload.profile
}

export async function addLeadSocialLink(
  leadKey: string,
  input: SocialLinkInput,
): Promise<LeadSocialLink> {
  const payload = await send<{ socialLink?: LeadSocialLink }>('/api/lead-crm/social', 'POST', {
    leadKey,
    ...input,
  })

  if (!payload.socialLink) throw new Error('The social profile link could not be saved.')

  return payload.socialLink
}

export async function updateLeadSocialLink(
  leadKey: string,
  id: string,
  input: SocialLinkInput,
): Promise<LeadSocialLink> {
  const payload = await send<{ socialLink?: LeadSocialLink }>('/api/lead-crm/social', 'PUT', {
    leadKey,
    id,
    ...input,
  })

  if (!payload.socialLink) throw new Error('The social profile link could not be saved.')

  return payload.socialLink
}

export async function removeLeadSocialLink(leadKey: string, id: string): Promise<string> {
  await send('/api/lead-crm/social', 'DELETE', { leadKey, id })
  return id
}

export async function createLeadInteraction(
  leadKey: string,
  input: InteractionInput,
): Promise<LeadInteraction> {
  const payload = await send<{ interaction?: LeadInteraction }>('/api/lead-crm/interaction', 'POST', {
    leadKey,
    ...input,
  })

  if (!payload.interaction) throw new Error('The interaction could not be saved.')

  return payload.interaction
}
